import React, { useState, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { CodeBracketIcon, EyeIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/helpers';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  placeholder?: string;
  className?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  error,
  placeholder = 'Start typing...',
  className,
}) => {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value);

  // Sync htmlValue when value prop changes
  useEffect(() => {
    setHtmlValue(value);
  }, [value]);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
      ],
    }),
    []
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'align',
    'link',
  ];

  const handleWysiwygChange = (content: string) => {
    setHtmlValue(content);
    onChange(content);
  };

  const handleHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setHtmlValue(newValue);
    onChange(newValue);
  };

  const toggleMode = () => {
    setIsHtmlMode(!isHtmlMode);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-secondary-700">
            {label}
          </label>
          <button
            type="button"
            onClick={toggleMode}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
          >
            {isHtmlMode ? (
              <>
                <EyeIcon className="h-3.5 w-3.5" />
                Visual Editor
              </>
            ) : (
              <>
                <CodeBracketIcon className="h-3.5 w-3.5" />
                Edit HTML
              </>
            )}
          </button>
        </div>
      )}

      <div
        className={cn(
          'rounded-lg border overflow-hidden transition-colors',
          error ? 'border-danger-300' : 'border-secondary-200',
          'focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-300'
        )}
      >
        {isHtmlMode ? (
          <div className="relative">
            <div className="absolute top-2 right-2 px-2 py-0.5 text-xs font-medium bg-secondary-100 text-secondary-600 rounded">
              HTML
            </div>
            <textarea
              value={htmlValue}
              onChange={handleHtmlChange}
              placeholder={placeholder}
              className="w-full min-h-[300px] p-4 pt-8 font-mono text-sm text-secondary-900 bg-secondary-50 resize-y focus:outline-none"
              spellCheck={false}
            />
          </div>
        ) : (
          <div className="email-editor">
            <ReactQuill
              theme="snow"
              value={htmlValue}
              onChange={handleWysiwygChange}
              modules={modules}
              formats={formats}
              placeholder={placeholder}
              className="[&_.ql-container]:min-h-[250px] [&_.ql-container]:text-sm [&_.ql-container]:font-sans [&_.ql-editor]:min-h-[250px]"
            />
          </div>
        )}
      </div>

      {error && <p className="text-sm text-danger-500">{error}</p>}

      <p className="text-xs text-secondary-500">
        Use template variables like{' '}
        <code className="px-1 py-0.5 bg-secondary-100 rounded text-secondary-700">
          {`{{firstName}}`}
        </code>{' '}
        to personalize your emails.
      </p>
    </div>
  );
};

export default RichTextEditor;



